import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  MenuItem,
  DialogActions,
  Box,
  Typography,
  TextField,
} from "@material-ui/core";

import { makeStyles, createStyles } from "@material-ui/core/styles";
import {
  updateLicense,
  changeLicenseStatus,
  cancelChangeRequest,
} from "../../../../services/locationApp/licenseService";
import TextfieldDefault from "../../../../components/textField/TextfieldDefaultNew";
import { CustomTheme } from "../../../../types/customTheme";
import AlertDialog from "../../../../components/alerts/AlertDialog";
import { getIsAuthorized } from "../../../../utils/authorities";
import Authorities from "../../../../auth/authorities";
import ButtonCommon from "../../../../components/buttons/ButtonCommon";
import { buttonColors } from "../../../../utils/enum";
import LockTypeDialog from "./LockTypeDialog";
import { useTheme } from "@material-ui/core/styles";
import Cookies from "js-cookie";


const useStyles = makeStyles((theme: CustomTheme) =>
  createStyles({
    dialogPaper: {
      borderRadius: 10,
    },
    dropdown: {
      backgroundColor: theme.palette.background.entity_background,
      borderRadius: 10,
      "& .MuiOutlinedInput-root": {
        borderRadius: 10,
        backgroundColor: theme.palette.background.entity_background,
      },
      "& .MuiOutlinedInput-notchedOutline": {
        border: "none",
      },
    },
    dropdownMenuPaper: {
      backgroundColor: theme.palette.background.entity_background,
      borderRadius: 10,
      boxShadow: "0px 4px 12px rgba(0,0,0,0.08)",
      padding: theme.spacing(1),
    },
    errorText: {
      color: theme.palette.custom.red.main,
      fontSize: 12,
      marginTop: -8,
      marginBottom: 4,
    },
    statusPill: {
      borderRadius: 8,
      padding: "2px 8px",
      fontSize: "12px",
      textAlign: "center",
      fontWeight: 600,
      minWidth: 70,
      display: "inline-block",
      width: "fit-content",
    },
  }),
);

interface License {
  licenseCode: string;
  subscriptionId?: string;
  name: string;
  deviceType: string;
  deviceId: string;
  status: string;
  subscriptionPeriod: string;
  subscriptionAmount: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  license: License | null;
  locationId: string;
  onUpdate: (licenseCode: string, patch: Partial<License>) => void;
  onReload: () => Promise<void> | void;
}
const getToken = () => Cookies.get("idToken") || null;


const LicenseEditModal: React.FC<Props> = ({
  open,
  onClose,
  license,
  locationId,
  onUpdate,
  onReload,
}) => {
  const classes = useStyles();
  const [name, setName] = useState("");
  const [amountError, setAmountError] = useState("");
  const [amount, setAmount] = useState("0.00");
  const [period, setPeriod] = useState("monthly");
  const [originalValues, setOriginalValues] = useState({
    name: "",
    amount: "0.00",
    period: "monthly",
  });
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showStatusConfirm, setShowStatusConfirm] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<string | null>(null);
  const theme = useTheme<CustomTheme>();

  const getStatusStyles = (status: string) => {
    const normalized = status.toLowerCase();
    const map: Record<string, { bg: string; text: string }> = {
      active: {
        bg: theme.palette.custom.green.main || "#4CAF50",
        text: theme.palette.custom.green.contrastText || "#fff",
      },
      trial: {
        bg: theme.palette.custom.blue.main || "#2196f3",
        text: theme.palette.custom.blue.contrastText || "#fff",
      },
      locked: {
        bg: theme.palette.custom.red.main || "#f44336",
        text: theme.palette.custom.red.contrastText || "#fff",
      },
      changing: {
        bg: theme.palette.custom.yellow.main || "#FFEB3B",
        text: theme.palette.custom.yellow.contrastText || "#000",
      },
    };
    return map[normalized] || { bg: "#ccc", text: "#000" };
  };

  useEffect(() => {
    if (open && license) {
      setName(license.name);
      setAmount(license.subscriptionAmount);
      setPeriod(license.subscriptionPeriod);
      setOriginalValues({
        name: license.name,
        amount: license.subscriptionAmount,
        period: license.subscriptionPeriod,
      });
      setAmountError("");
    }
  }, [open, license]);

  const validateAmount = (): boolean => {
    const decimalRegex = /^\d+\.\d{2}$/; // Require exactly two decimal digits
    if (!decimalRegex.test(amount) || parseFloat(amount) < 0) {
      setAmountError("Please enter a valid positive amount with two decimals");
      return false;
    }
    setAmountError("");
    return true;
  };

  const [showCancelRequestConfirm, setShowCancelRequestConfirm] =
    useState(false);

  const [showLockTypeDialog, setShowLockTypeDialog] = useState(false);
  const [selectedType, setSelectedType] = useState<
    "location-lock" | "device-lock" | "location-active" | "device-active"
  >("location-lock");

  const [showStatusTypeDialog, setShowStatusTypeDialog] = useState(false);

  const isChanged =
    name !== originalValues.name ||
    amount !== originalValues.amount ||
    period !== originalValues.period;

  const handleSaveConfirmed = async () => {
    if (!license) return;
    if (!getIsAuthorized(Authorities.LOCATION_LICENSE_UPDATE)) return;

    const isValid = validateAmount();
    if (!isValid) return;

    setShowSaveConfirm(false);
    setIsLoadingSave(true);

    try {
      const idToken = await getToken();
      await updateLicense(
        locationId,
        {
          licenseCode: license.licenseCode,
          name: name.trim(),
          subscriptionAmount: parseFloat(amount).toFixed(2),
          subscriptionPeriod: period,
        },
        idToken,
      );
      onUpdate(license.licenseCode, {
        name: name.trim(),
        subscriptionAmount: parseFloat(amount).toFixed(2),
        subscriptionPeriod: period,
      });
      onClose();
      setShowSaveConfirm(false);
      onClose();
    } catch (err) {
      console.error("PUT error →", err);
    } finally {
      setIsLoadingSave(false);
    }
  };
  const [isLoadingCancel, setIsLoadingCancel] = useState(false);

  const handleCancelChangeRequest = async () => {
    if (!license) return;
    if (!getIsAuthorized(Authorities.LOCATION_LICENSE_UPDATE)) return;

    // Close the confirm dialog right away
    setShowCancelRequestConfirm(false);

    setIsLoadingCancel(true);
    try {
      const idToken = await getToken();
      await cancelChangeRequest(
        locationId,
        {
          // deviceId: license.deviceId,
          licenseCode: license.licenseCode,
        },
        idToken,
      );

      onUpdate(license.licenseCode, {
        status: license.status,
      });
      await onReload();
      onClose();
    } catch (err) {
      console.error("POST cancel change request error →", err);
    } finally {
      setIsLoadingCancel(false);
    }
  };

  const [isLoadingSave, setIsLoadingSave] = useState(false);
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);

  const handleStatusChangeConfirmed = async () => {
    if (!license || !pendingStatus) return;
    if (!getIsAuthorized(Authorities.LOCATION_LICENSE_UPDATE)) return;

    setShowStatusConfirm(false);
    setIsLoadingStatus(true);
    try {
      const idToken = await getToken();
      await changeLicenseStatus(
        locationId,
        {
          licenseCode: license.licenseCode,
          status: pendingStatus,
          // deviceId: license.deviceId,
          type: selectedType,
        },
        idToken,
      );

      onUpdate(license.licenseCode, {
        status: pendingStatus,
      });
      await onReload();
      onClose();
    } catch (err) {
      console.error("POST status change error →", err);
    } finally {
      setIsLoadingStatus(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={() => {
          if (isChanged) setShowCancelConfirm(true);
          else onClose();
        }}
        maxWidth="sm"
        fullWidth
        classes={{ paper: classes.dialogPaper }}
      >
        <DialogTitle>Edit License</DialogTitle>
        <DialogContent>
          {license ? (
            <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
              <Typography>
                <strong>License Code:</strong> {license.licenseCode}
              </Typography>
              <Typography>
                <strong>Device ID:</strong> {license.deviceId}
              </Typography>
              <Typography>
                <strong>Device Type:</strong> {license.deviceType}
              </Typography>
              <Typography>
                <strong>Subscription ID:</strong> {license.subscriptionId || "-"}
              </Typography>
              <Box display="flex" alignItems="center" style={{ gap: 5 }}>
                <Typography>
                  <strong>Status:</strong>
                </Typography>
                <span
                  className={classes.statusPill}
                  style={{
                    backgroundColor: getStatusStyles(license.status).bg,
                    color: getStatusStyles(license.status).text,
                  }}
                >
                  {license.status.charAt(0).toUpperCase() +
                    license.status.slice(1)}
                </span>
              </Box>

              <TextfieldDefault
                id="license-name"
                name="license-name"
                label="License Name"
                style={{ width: "100%" }}
                onChange={(e: any) => setName(e.target.value)}
                value={name}
                type="text"
              />

              <TextfieldDefault
                id="subscription-amount"
                name="subscription-amount"
                label="Subscription Amount"
                style={{ width: "100%" }}
                onChange={(e: any) => {
                  const value = e.target.value;
                  setAmount(value);

                  if (parseFloat(value) < 0) {
                    setAmountError("Amount cannot be negative");
                  } else {
                    setAmountError("");
                  }
                }}
                value={amount}
                type="number"
              />
              {amountError && (
                <Typography
                  style={{
                    color: "red",
                    fontSize: 12,
                    marginTop: -18, 
                    marginBottom: 4, 
                  }}
                >
                  {amountError}
                </Typography>
              )}

              <TextField
                select
                label="Subscription Period"
                variant="outlined"
                fullWidth
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className={classes.dropdown}
                SelectProps={{
                  MenuProps: {
                    classes: { paper: classes.dropdownMenuPaper },
                  },
                }}
              >
                {["monthly", "weekly", "annually", "one-off"].map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt.charAt(0).toUpperCase() + opt.slice(1)}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          ) : (
            <Typography>No license selected</Typography>
          )}
        </DialogContent>

        <DialogActions style={{ width: "100%", padding: "16px 24px" }}>
          <Box display="flex" justifyContent="space-between" width="100%">
            {license && license.status.toLowerCase() !== "changing" ? (
              <ButtonCommon
                variant="contained"
                color={license.status === "active" ? "blue" : "green"}
                onClick={() => {
                  setPendingStatus(
                    license.status === "active" ? "revoked" : "active",
                  );
                  setShowLockTypeDialog(true);
                }}
                isLoadingPage={isLoadingStatus}
                style={{
                  minWidth: 180,
                  height: 36,
                  whiteSpace: "nowrap",
                  padding: "6px 16px",
                }}
              >
                {license.status === "active"
                  ? "Revoke License"
                  : "Re-Activate License"}
              </ButtonCommon>
            ) : (
              <ButtonCommon
                variant="contained"
                color="red"
                onClick={() => setShowCancelRequestConfirm(true)}
                isLoadingPage={isLoadingCancel}
                style={{
                  minWidth: 212,
                  height: 36,
                  whiteSpace: "nowrap",
                  padding: "6px 16px",
                }}
              >
                Cancel Change Request
              </ButtonCommon>
            )}

            <Box display="flex" style={{ gap: 3 }}>
              <ButtonCommon
                variant="contained"
                color="orange"
                onClick={() => {
                  if (isChanged) setShowCancelConfirm(true);
                  else onClose();
                }}
                style={{ minWidth: 120, height: 36 }}
              >
                Cancel
              </ButtonCommon>

              <ButtonCommon
                variant="contained"
                color="yellow"
                onClick={() => {
                  const isValid = validateAmount();
                  if (isValid) setShowSaveConfirm(true);
                }}
                disabled={!isChanged || !!amountError}
                isLoadingPage={isLoadingSave}
                style={{ minWidth: 120, height: 36 }}
              >
                Save
              </ButtonCommon>
            </Box>
          </Box>
        </DialogActions>
      </Dialog>
      <LockTypeDialog
        open={showLockTypeDialog}
        onClose={() => setShowLockTypeDialog(false)}
        onSelect={(type) => {
          setSelectedType(type);
          setShowStatusConfirm(true);
        }}
        mode={pendingStatus === "active" ? "activate" : "lock"}
      />

      {/* Confirm Save Dialog */}
      <AlertDialog
        open={showSaveConfirm}
        confirmAction={handleSaveConfirmed}
        title="Confirm Changes"
        desc="Are you sure you want to save these changes to the license?"
        severity="info"
        confirmLabel="Yes"
        cancelLabel="No"
        cancelAction={() => setShowSaveConfirm(false)}
        disableBackdropClick
        color="secondary"
      />

      {/* Cancel Warning */}
      <AlertDialog
        open={showCancelConfirm}
        confirmAction={() => {
          setShowCancelConfirm(false);
          onClose();
        }}
        title="Unsaved Changes"
        desc="You have unsaved changes. Are you sure you want to cancel?"
        severity="warning"
        confirmLabel="Yes"
        cancelLabel="No"
        cancelAction={() => setShowCancelConfirm(false)}
        disableBackdropClick
        color="secondary"
      />

      {/* Revoke/Active Confirmation */}
      <AlertDialog
        open={showStatusConfirm}
        confirmAction={handleStatusChangeConfirmed}
        title={pendingStatus === "active" ? "Active License" : "Revoke License"}
        desc={`Are you sure you want to ${
          pendingStatus === "active" ? "active" : "revoke"
        } this license?`}
        severity={pendingStatus === "active" ? "success" : "error"}
        confirmLabel="Yes"
        cancelLabel="No"
        cancelAction={() => setShowStatusConfirm(false)}
        disableBackdropClick
        color={pendingStatus === "active" ? "secondary" : "secondary"}
      />
      <AlertDialog
        open={showCancelRequestConfirm}
        confirmAction={handleCancelChangeRequest}
        title="Remove Change Request"
        desc="Are you sure you want to remove this change request?"
        severity="warning"
        confirmLabel="Yes"
        cancelLabel="No"
        cancelAction={() => setShowCancelRequestConfirm(false)}
        disableBackdropClick
        color="secondary"
      />
    </>
  );
};

export default LicenseEditModal;
